import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { detectInAppBrowser } from '@/lib/posthog/inAppBrowser';
import { AuthError } from '@/modules/auth/components/AuthError';
import { GoogleLogin } from '@/modules/auth/components/GoogleLogin';
import { LinkedInLogin } from '@/modules/auth/components/LinkedInLogin';
import { SlackLogin } from '@/modules/auth/components/SlackLogin';
import {
  type AuthMethod,
  businessEmailRequiredMessage,
  getAuthFailureReason,
  isBusinessEmailRequiredError,
  isInvalidTrialOfferError,
  trialOfferFailureMessage,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import { useLinkedInSsoEnabled } from '@/modules/auth/hooks/useLinkedInSsoEnabled';
import { detectMobileOs } from '@/modules/auth/inAppHandoff';
import { getSignUpParams } from '@/modules/auth/signUpParams';
import { isSlackSsoConfigured } from '@/modules/auth/slackSso';
import { TermsNotice } from '@/modules/legal/components/terms-notice/TermsNotice';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';

import { ContinueOnComputer } from './continueOnComputer/ContinueOnComputer';
import { InAppGoogleSignUp } from './inAppGoogle/InAppGoogleSignUp';
import { SignUpLayout } from './signUpLayout/SignUpLayout';

const SIGN_UP_FAILED_MESSAGE = 'We couldn’t create the account. Try again.';

export const SignUp = () => {
  const [searchParams] = useSearchParams();
  const isLinkedInSsoEnabled = useLinkedInSsoEnabled();
  const terms = useLegalDocumentsQuery().data?.find(
    (document) => document.type === 'TermsAndConditions',
  );
  const acceptTerms = Boolean(terms?.url);

  const trialOfferCode = searchParams.get(trialOfferParamKey) ?? undefined;
  const { oauthReturnTo } = getSignUpParams(searchParams);

  const trackEvent = useTrackEvent();
  const inAppBrowser = detectInAppBrowser(navigator.userAgent);

  // Every method signs up by redirect and finishes on its callback page, so
  // only a sign-up that fails before leaving the page shows an error here.
  const [error, setError] = useState<string | null>(null);

  const handleError = (authMethod: AuthMethod, error?: Error) => {
    trackEvent('signUpFailed', {
      authMethod,
      reason: getAuthFailureReason(error, authMethod),
    });
    if (isBusinessEmailRequiredError(error)) {
      setError(businessEmailRequiredMessage);
      return;
    }

    setError(
      trialOfferCode && isInvalidTrialOfferError(error)
        ? trialOfferFailureMessage
        : SIGN_UP_FAILED_MESSAGE,
    );
  };

  const offerNotice = trialOfferCode
    ? 'One month of Finlay Source, free. No card required – continue on Free afterward.'
    : 'No credit card required.';

  const slackLogin = isSlackSsoConfigured && (
    <SlackLogin
      returnTo={oauthReturnTo}
      acceptTerms={acceptTerms}
      intent="signUp"
      onError={(error) => handleError('Slack', error)}
    />
  );

  return (
    <SignUpLayout hasTrialOffer={Boolean(trialOfferCode)}>
      {/* The sign-up buttons stay at most 400px wide. */}
      <div className="mx-auto w-full max-w-[400px] space-y-3">
        {inAppBrowser !== 'none' ? (
          // Google blocks sign-in in apps' built-in browsers, so Slack, which
          // signs in without leaving the app, leads and Google hands off to
          // the real browser. LinkedIn is desktop-only (`useLinkedInSsoEnabled`).
          <>
            {slackLogin}
            <InAppGoogleSignUp
              app={inAppBrowser}
              os={detectMobileOs(navigator.userAgent, navigator.maxTouchPoints)}
            />
          </>
        ) : (
          <>
            <GoogleLogin
              returnTo={oauthReturnTo}
              acceptTerms={acceptTerms}
              intent="signUp"
              onError={(error) => handleError('Google', error)}
            />
            {slackLogin}
            {isLinkedInSsoEnabled && (
              <LinkedInLogin
                returnTo={oauthReturnTo}
                acceptTerms={acceptTerms}
                intent="signUp"
                onError={(error) => handleError('LinkedIn', error)}
              />
            )}
          </>
        )}
      </div>
      {error && <AuthError message={error} />}
      <p className="text-muted-foreground text-center text-[13px]">
        {offerNotice}
      </p>
      <ContinueOnComputer />
      <hr className="border-border" />
      <TermsNotice
        lead="By signing up"
        terms={terms}
        className="text-muted-foreground text-center text-xs leading-[1.6]"
      />
    </SignUpLayout>
  );
};
